import { Autocomplete, Chip, Paper, TextField } from '@mui/material';
import { useLocalObservable } from 'mobx-react-lite';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';

import { listboxStyles, paperStyles } from '@/components/Selector/styles';
import { ShowAllToggleOption } from '@/components/ShowAllToggleOption';
import { UserOptionItem } from '@/components/UserOptionItem';
import { useRootStore, useUserStore } from '@/configs/store';
import { useFavoriteUsers } from '@/features/favoriteUsers';
import { useLogsMode } from '@/features/logsMode';
import { UserSuggestModel } from '@/models/UserSuggestModel';
import { MuiColors } from '@/types/colors';
import { YandexUser } from '@/types/yandex';

const SHOW_ALL_TOGGLE_UID = '__show_all_toggle__';

const TOGGLE_OPTION: YandexUser = {
  uid: SHOW_ALL_TOGGLE_UID,
  display: '',
  email: '',
};

type UserSelectorProps = {
  value: YandexUser[];
  onChange: (users: YandexUser[]) => void;
};

const UserSelector: React.FC<UserSelectorProps> = observer(({ value, onChange }) => {
  const { user } = useUserStore();
  const { userStore, alertsStore } = useRootStore();
  const favoriteUsersModel = useFavoriteUsers();
  const mode = useLogsMode();

  // Local instance of UserSuggestModel for this form
  const suggestModel = useLocalObservable(
    () => new UserSuggestModel(userStore, alertsStore, favoriteUsersModel, mode),
  );

  const { toggleFavoriteUser, isFavorite } = favoriteUsersModel;

  // Add toggle option to the beginning of options
  const optionsWithToggle = useMemo(
    () => [TOGGLE_OPTION, ...suggestModel.options],
    [suggestModel.options],
  );

  return (
    <Autocomplete
      multiple
      size="small"
      options={optionsWithToggle}
      filterOptions={(options) => options}
      getOptionLabel={(option) => (option.uid === SHOW_ALL_TOGGLE_UID ? '' : option.display)}
      value={value || []}
      onChange={(_, newValue) => {
        const filteredValue = newValue.filter((user) => user.uid !== SHOW_ALL_TOGGLE_UID);
        onChange(filteredValue);
      }}
      onOpen={() => {
        suggestModel.setSuggestQuery('');
      }}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'input') {
          suggestModel.setSuggestQuery(newInputValue);
        }
      }}
      isOptionEqualToValue={(option, value) => option.uid === value.uid}
      loading={suggestModel.loadingStage.isLoading}
      slots={{
        paper: Paper,
      }}
      slotProps={{
        paper: {
          sx: paperStyles,
        },
        listbox: {
          sx: {
            ...listboxStyles,
            paddingTop: '6px',
            paddingBottom: '6px',
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Выберите пользователей"
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              minHeight: 38,
              height: 'auto',
              maxHeight: 120,
              overflow: 'auto',
              padding: '4px',
              '& fieldset': {
                borderRadius: '6px',
                borderColor: MuiColors.secondaryColor,
              },
              '& input': {
                padding: '4px 8px',
                height: 24,
                boxSizing: 'border-box',
                color: MuiColors.primaryColor,
                fontFamily: 'Montserrat',
              },
              '& .MuiAutocomplete-tag': {
                margin: '2px',
                maxWidth: '200px',
              },
            },
          }}
        />
      )}
      renderValue={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip label={option.display} {...getTagProps({ index })} key={option.uid} size="small" />
        ))
      }
      noOptionsText="Пользователи не найдены"
      loadingText="Загрузка пользователей..."
      renderOption={(props, option) => {
        if (option.uid === SHOW_ALL_TOGGLE_UID) {
          return (
            <ShowAllToggleOption
              key={option.uid}
              checked={suggestModel.showAll}
              onToggle={suggestModel.toggleShowAll}
              props={props}
              margin="4px 8px"
            />
          );
        }

        return (
          <UserOptionItem
            option={option}
            currentUserId={user?.id}
            isFavorite={isFavorite}
            onFavoriteClick={toggleFavoriteUser}
            props={props}
          />
        );
      }}
    />
  );
});

export default UserSelector;
